import PropTypes from 'prop-types';
import React from 'react';
import computeLayout from "css-layout";
import autobind from "autobind-decorator";
import dom from "abusesa-js/ui/dom";

import mix from "lib/mixin";

export {default as measureText} from "lib/measureText";

class Updater {
    _request = null

    update() {
        if (!this._request) {
            this._request = requestAnimationFrame(()=> {
                this._request = null;
                this.doUpdate();
            });
        }
    }

    doUpdate() {
    }
}

const getDevicePixelRatio = () => {
    let dpr = window.devicePixelRatio || 1;
    let ua = navigator.userAgent;
    // safari (at least still as of dev preview 10.2) has very ugly and aliased text
    // rendering on canvas for non-retina screens. As a work around, we double the ratio
    // to do manual anti-aliasing for non-retina screens
    const isSafari = () => ua.indexOf("Safari") >= 0 && ua.indexOf("Chrom") === -1;
    return isSafari() && dpr < 2 ? dpr * 2 : dpr;
};

class LayerContext {
    static contextTypes = {
        registerLayer: PropTypes.func,
        unregisterLayer: PropTypes.func,
        requestRedraw: PropTypes.func,
        clearLayout: PropTypes.func,
        getLayout: PropTypes.func
    };

    componentWillUnmount() {
        this.unregister();
    }


    getLayout() {
        return this.context.getLayout(this, false);
    }

    register() {
        this.context.registerLayer(this);
    }

    unregister() {
        this.context.unregisterLayer(this);
        this.context.clearLayout();
    }

    requestRedraw() {
        this.context.requestRedraw();
    }

    redraw() {
        this.requestRedraw();
    }
}


class ProvideLayerContext {
    static childContextTypes = {
        registerLayer: PropTypes.func,
        unregisterLayer: PropTypes.func,
        requestRedraw: PropTypes.func,
        clearLayout: PropTypes.func,
        getLayout: PropTypes.func
    };


    layers = null

    getChildContext() {
        if (!this.layers) {
            this.layers = [];
        }

        return {
            registerLayer: layer => this.layers.push(layer),
            unregisterLayer: layer => this.layers.splice(this.layers.indexOf(layer), 1),
            requestRedraw: () => this.redraw(),
            clearLayout: () => this.clearLayout(),
            getLayout: (layer, includeFullNode) => this.getLayoutFor(layer, includeFullNode)
        };
    }

    getLayers() {
        return this.layers || [];
    }
}

class LayerBase {
    _size = null

    get defaultStyles() {
        return {};
    }

    getHoverData({x, y}) {
        return null;
    }

    generateLayoutNode() {
        return {
            style: this.getStyles(),
            node: this
        };
    }

    getStyles(props = this.props) {
        let styles = Object.assign({}, this.defaultStyles, props.style);
        if (!styles.flex) {
            let size = this.getEstimatedSize();
            styles = Object.assign(size, styles);
        }
        return styles;
    }

    getEstimatedSize() {
        return this.getSize();
    }

    getSize() {
        return this._size || {width: 0, height: 0};
    }

    setSize({width, height}) {
        this._size = {width, height};
    }
}

export class Layer extends mix(React.PureComponent, LayerContext, LayerBase) {
    _canvas = null
    _size = null

    constructor(...args) {
        super(...args);
        this.state = {dirty: true};

        this.register();
        this.getCanvas();
    }

    componentDidMount() {
        if (this.isDirty()) {
            this.requestRedraw();
        }
    }

    _rescale(ctx = this.getCtx()) {
        let ratio = getDevicePixelRatio();
        ctx.imageSmoothingQuality = "high";
        ctx.scale(ratio, ratio);
    }

    clear() {
        let canvas = this.getCanvas();
        // canvas.width = canvas.width;
        // this._rescale();
        this.getCtx().clearRect(0,0, canvas.width / 2, canvas.height);
        this.getCtx().clearRect( canvas.width / 2, 0, canvas.width / 2, canvas.height);
    }

    setSize({width, height}) {
        super.setSize({width, height});
        let ratio = getDevicePixelRatio();

        width = Math.round(ratio * width);
        height = Math.round(ratio * height);

        let canvas = this.getCanvas();
        if (canvas.width != width || canvas.height != height) {
            canvas.height = height;
            canvas.width = width;
            this._rescale();
            this.markDirty();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.isDirty()) {
            this.requestRedraw();
        }
    }

    getCanvas() {
        if (!this._canvas) {
            this._canvas = document.createElement("canvas");
            this.setSize(this.getSize());
        }

        return this._canvas;
    }

    cleanup() {
        if (this._canvas) {
            this._canvas.width = 0;
            this._canvas.height = 0;
        }

        this._canvas = null;
    }

    getCtx() {
        return this.getCanvas().getContext("2d");
    }

    isDirty() {
        return this.state.dirty;
    }

    markDirty() {
        if (!this.isDirty()) {
            this.setState({dirty: true});
        }
    }

    draw() {
        if (this.isDirty() || !this._canvas) {
            this.clear();
            this.doDraw();
            this.setState({dirty: false});
        }
    }

    doDraw() {

    }

    render() {
        return null;
    }
}

const LayoutCache = new WeakMap();

class LayouterMixin extends ProvideLayerContext {
    generateLayoutNode() {
        let children = this.getLayers().map(layer => layer.generateLayoutNode());

        return {
            style: this.getStyles(),
            node: this,
            children
        };
    }

    clearLayout() {
        if (LayoutCache.get(this)) {
            LayoutCache.set(this, null);
        } else if (this.context.clearLayout) {
            this.context.clearLayout();
        }
    }

    getHoverData({x, y}) {
        for (let child of this.getLayers()) {
            if (child.getHoverData) {
                let layout = this.getLayoutFor(child);
                if (x >= layout.left && x <= (layout.left + layout.width)
                    && y >= layout.top && y <= layout.top + layout.height) {
                    let data = child.getHoverData({x: x - layout.left, y: y - layout.top});
                    if (data) {
                        return data;
                    }
                }
            }
        }
    }

    cleanup() {
        if (super.cleanup) {
            super.cleanup();
        }

        for (let child of this.getLayers()) {
            if (child.cleanup) {
                child.cleanup();
            }
        }
    }

    getLayoutFor(layer, includeFullNode = false) {
        let layout;

        if (this.context.getLayout) {
            layout = this.context.getLayout(this, true);
        } else if (LayoutCache.get(this)) {
            layout = LayoutCache.get(this);
        } else {
            layout = this.generateLayoutNode();
            computeLayout(layout);
            LayoutCache.set(this, layout);
        }

        let res = layout.children
            .filter(({node}) => node == layer)[0];

        return includeFullNode ? res : res.layout;
    }

    *getChildCanvases() {
        for (let child of this.getLayers()) {
            let layout = this.getLayoutFor(child);

            child.setSize({width: layout.width, height: layout.height});
            if (layout.left < 0){
                continue;
            }

            if (child.getChildCanvases) {
                for (let {canvas, position, opacity} of child.getChildCanvases()) {
                    yield {
                        canvas,
                        opacity,
                        position: {x: position.x + layout.left, y: position.y + layout.top}
                    };
                }
            } else if (child.getCanvas) {
                let canvas = child.getCanvas();
                child.draw();
                yield {
                    canvas,
                    position: {x: layout.left, y: layout.top},
                    opacity: 1
                };
            }
        }
    }
}


export class LayerGroup extends mix(React.PureComponent, LayerBase, ProvideLayerContext, LayerContext, LayouterMixin) {
    constructor(...args) {
        super(...args);
        this.register();
    }

    render() {
        return <noscript>{this.props.children}</noscript>;
    }
}

export class FlexLayerGroup extends LayerGroup {
    get defaultStyles() {
        return Object.assign(super.defaultStyles, {
            flex: 1
        });
    }
}

export class Surface extends mix(React.Component, Updater, ProvideLayerContext, LayouterMixin) {
    static propTypes = {
        width: PropTypes.number,
        height: PropTypes.number,
        isSelected: PropTypes.bool,
        style: PropTypes.object,
        setToolTip: PropTypes.func,
        clearToolTip: PropTypes.func
    };

    static displayName = 'Surface';

    get defaultStyles() {
        return {};
    }

    constructor() {
        super();
        this.state = {canvasFileUrl: null};
    }

    getStyles() {
        let size = {width: this.props.width, height: this.props.height};
        return Object.assign({}, this.defaultStyles, size, this.props.style);
    }

    _rescale() {
        if (!this.getCanvas()) {
            return;
        }

        let ratio = getDevicePixelRatio();

        let width = Math.round(this.props.width * ratio);
        let height = Math.round(this.props.height * ratio);

        let canvas = this.getCanvas();

        if (canvas.width != width || canvas.height != height) {
            canvas.height = height;
            canvas.width = width;

            canvas.style.width = this.props.width + "px";
            canvas.style.height = this.props.height + "px";
        }
    }

    cleanup() {
        for (let child of this.getLayers()) {
            if (child.cleanup) {
                child.cleanup();
            }
        }
    }

    getCanvas() {
        return this.canvasRef;
    }

    getCtx() {
        let canvas = this.getCanvas();
        return canvas && canvas.getContext ? canvas.getContext("2d") : null;
    }

    componentDidMount() {
        this._rescale();
    }

    componentWillUnmount() {
        this.cleanup();
    }

    redraw() {
        this.update();
    }

    @autobind
    handleMouseMove(e) {
        if (!this.props.isSelected) {
            return;
        }

        let el = this.getCanvas();
        if (!el) {
            this.props.clearToolTip();
            return;
        }
        let bounds = el.getBoundingClientRect();
        let pos = {
            x: e.clientX - bounds.left,
            y: e.clientY - bounds.top
        };

        let data = this.getHoverData(pos);
        if (data) {
            this.props.setToolTip({x: e.clientX, y: e.clientY, contents: data});
        } else {
            this.props.clearToolTip();
        }
    }

    @autobind
    handleMouseLeave(e) {
        this.props.clearToolTip();
    }

    @autobind
    doUpdate() {
        this.clearLayout();

        let canvas = this.getCanvas();
        if (!canvas){
            return;
        }
        canvas.width = this.getCanvas().width;
        let ctx = this.getCtx();

        ctx.beginPath();

        let ratio = getDevicePixelRatio();

        ctx.fillStyle = "white";
        ctx.fillRect(0,0, canvas.width, canvas.height);

        for (let {canvas, position, opacity} of this.getChildCanvases()) {
            if (canvas.height != 0 && canvas.width != 0) {
                ctx.save();
                ctx.translate(Math.round(position.x * ratio), Math.round(position.y * ratio));

                if (opacity && opacity !== 1) {
                    ctx.globalAlpha = opacity;
                }

                ctx.drawImage(canvas, 0, 0);
                // ctx.strokeStyle = "black"
                // ctx.rect(0,0, canvas.width, canvas.height);
                // ctx.stroke();
                ctx.restore();
            }
        }
    }

    componentDidUpdate(lastProps, lastState) {
        this._rescale();
        this.update();
    }

    takeScreenshot(name = "screenshot") {
        let canvas = this.getCanvas();
        let dataurl = canvas.toDataURL("image/png");

        // Compatibility note:
        // Safari 7 doesn't allow exporting canvas
        // when it has images drawn from data-urls.
        //
        // Safari 8 doesn't support "download" attribute
        // and thus cannot set the name of downloaded file.
        // Furthermore, in some cases safari 8 opens data urls
        // with content type that suggests an image in the browser
        // window instead of downloading (image/png, application/octet-stream etc),
        // thus requiring an invalid content type, such as image/screenshot.

        if (typeof document.createElement("a").download === "undefined" &&
            window.navigator.userAgent.indexOf("Safari") > -1) {
            dataurl = dataurl.replace(/^data:image\/png/, "data:image/screenshot");
        }


        let a = dom.createElement("a", {
            attributes: {
                download: name + ".png",
                href: dataurl,
                target: "_self"
            },
            text: "download",
            style: {
                position: "absolute",
                visibility: "hidden"
            }
        });

        document.body.appendChild(a);
        a.click();
        dom.destroyElement(a);
    }

    render() {
        let {width, height, children} = this.props;

        return <canvas
            ref={el => this.canvasRef = el}
            onMouseMove={this.handleMouseMove}
            onMouseLeave={this.handleMouseLeave}
            width={width}
            height={height}
        >{children}</canvas>;
    }
}


// WEBPACK FOOTER //
// ./src/kpi/charts/Canvas.jsx